/**
 * Who a User level template is shared with, inside its access card: the
 * owner's avatar (with a lock: they always have access), the people it is
 * shared with, a dashed add button and the member count. The button opens a
 * searchable list of the account's people; clicking one adds or removes them.
 * The owner is not in the list. Someone no longer in the account stays on it
 * under the name saved with them, so they can still be removed.
 *
 *   members   [{ mondayUserId, mondayUserName }] (lib/accessMembers)
 *   ownerId   the template's owner; the person editing, on a new template
 *   viewerId  the person editing, named "You" until the account's people load
 *   people    useAccountUsers: { status, users, error }
 *   onChange(members)
 */
import { Avatar, Search } from '@vibe/core';
import { Check, Invite, Locked } from '@vibe/icons';
import React, { useRef, useState } from 'react';
import { avatarColor, initialsOf } from '../../lib/avatars';
import AnchoredPopover from './AnchoredPopover';

const SHOWN = 5; // avatars before "+N"

// Vibe's Avatar shows its aria-label as a tooltip
const PersonAvatar = ({ id, name, label, withoutTooltip }) => (
  <Avatar
    size="medium"
    type="text"
    text={initialsOf(name)}
    customBackgroundColor={avatarColor(id)}
    textClassName="spt-share__initials"
    aria-label={label || name}
    withoutTooltip={withoutTooltip}
  />
);

export default function AccessMembersPicker({ members, ownerId, viewerId, people, onChange }) {
  const addRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // who was on the list when it opened: they stay on top while it is open
  const [pinned, setPinned] = useState(() => new Set());
  const list = members || [];
  const users = (people && people.users) || [];
  const same = (a, b) => a != null && b != null && String(a) === String(b);
  const nameOf = (id, saved) => {
    const user = users.find((u) => same(u.id, id));
    if (user && user.name) return user.name;
    if (saved) return saved;
    return same(id, viewerId) ? 'You' : `User ${id}`;
  };
  const isOn = (id) => list.some((m) => same(m.mondayUserId, id));

  const openList = () => {
    setPinned(new Set(list.map((m) => String(m.mondayUserId))));
    setQuery('');
    setOpen(true);
  };
  const toggle = (row) =>
    onChange(isOn(row.id) ? list.filter((m) => !same(m.mondayUserId, row.id)) : [...list, { mondayUserId: Number(row.id), mondayUserName: row.name }]);

  // the account's people but the owner, and anyone on the list no longer among them
  const everyone = [
    ...users.filter((u) => !same(u.id, ownerId)).map((u) => ({ id: String(u.id), name: u.name || `User ${u.id}` })),
    ...list.filter((m) => !users.some((u) => same(u.id, m.mondayUserId))).map((m) => ({ id: String(m.mondayUserId), name: nameOf(m.mondayUserId, m.mondayUserName) })),
  ];
  const q = query.trim().toLowerCase();
  const rows = everyone
    .filter((r) => !q || r.name.toLowerCase().includes(q))
    .sort((a, b) => Number(pinned.has(b.id)) - Number(pinned.has(a.id)));

  const shown = list.slice(0, SHOWN);
  const hidden = list.slice(SHOWN);
  const count = list.length + 1; // the owner, and the people it is shared with
  const loading = people && people.status === 'loading';

  return (
    <div className="spt-share" data-testid="share-with">
      <div className="spt-share__stack">
        {ownerId != null && (
          <span className="spt-share__owner">
            <PersonAvatar id={ownerId} name={nameOf(ownerId)} label={`${nameOf(ownerId)} (owner)`} />
            <span className="spt-share__lock" aria-hidden="true">
              <Locked size="10" />
            </span>
          </span>
        )}
        {shown.map((m) => (
          <PersonAvatar key={m.mondayUserId} id={m.mondayUserId} name={nameOf(m.mondayUserId, m.mondayUserName)} />
        ))}
        {hidden.length > 0 && (
          <span className="spt-share__more" title={hidden.map((m) => nameOf(m.mondayUserId, m.mondayUserName)).join(', ')}>
            +{hidden.length}
          </span>
        )}
        <button
          ref={addRef}
          type="button"
          className="spt-share__add"
          aria-label="Share with people"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={openList}
        >
          <Invite size="18" aria-hidden="true" />
        </button>
      </div>
      <span className="spt-share__count">
        {count} member{count === 1 ? '' : 's'}
      </span>

      {open && (
        <AnchoredPopover anchorEl={addRef.current} onClose={() => setOpen(false)} width={300}>
          <Search size="small" placeholder="Search members…" inputAriaLabel="Search members" value={query} onChange={setQuery} autoFocus />
          <div className="spt-menu-list spt-share-list" role="listbox" aria-multiselectable="true" aria-label="Members">
            {rows.map((r) => {
              const on = isOn(r.id);
              return (
                <button key={r.id} type="button" role="option" aria-selected={on} className="spt-menu-row spt-share-row" onClick={() => toggle(r)}>
                  <PersonAvatar id={r.id} name={r.name} withoutTooltip />
                  <span className="spt-menu-row-title">{r.name}</span>
                  {on && <Check className="spt-share-row__check" size="18" aria-hidden="true" />}
                </button>
              );
            })}
            {rows.length === 0 && (
              <div className="spt-menu-empty">
                {loading
                  ? 'Loading people…'
                  : people && people.error && !users.length
                    ? `The account's people could not be read: ${people.error}`
                    : q
                      ? 'No one matches'
                      : 'No one else is in this account'}
              </div>
            )}
          </div>
          <div className="spt-menu-hint spt-share-hint">They see it in their templates and automations, and can edit it.</div>
        </AnchoredPopover>
      )}
    </div>
  );
}
